// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import './styles.css';
import { loadDataset, search, type Dataset, type SearchHit } from './data';
import type { ViewId } from './types';
import type { ViewContext } from './viewContext';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossaryPopover';
import { createAbout } from './components/about';
import { createDrawers } from './components/drawer';
import { catInfo, jurisColour } from './domain';
import { renderOverview } from './views/overview';
import { renderFlow } from './views/flow';
import { renderMaterials } from './views/materials';
import { renderMap } from './views/map';
import { renderTrends } from './views/trends';
import { renderStreams } from './views/streams';
import { renderTreatment } from './views/treatment';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';
import { escapeHtml, fyShort } from './utils/format';

// Nav tabs are words only — never count badges.
const VIEWS: Array<{ id: ViewId; label: string; render: (root: HTMLElement, ctx: ViewContext) => void }> = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'flow', label: 'Where it goes', render: renderFlow },
  { id: 'materials', label: 'Materials', render: renderMaterials },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'trends', label: 'Trends', render: renderTrends },
  { id: 'streams', label: 'Streams', render: renderStreams },
  { id: 'treatment', label: 'Treatment', render: renderTreatment },
  { id: 'explorer', label: 'Explore', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
];

const LOGO = `<svg viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="7" fill="#2f8f4e"/><g fill="#ffffff"><path d="M16 6.4l3.6 6.2h-2.3l-2.6 0 0-2.3 2.3 0-2.6-4.5z"/><path d="M16 6.4l3.6 6.2h-2.3l-2.6 0 0-2.3 2.3 0-2.6-4.5z" transform="rotate(120 16 16)"/><path d="M16 6.4l3.6 6.2h-2.3l-2.6 0 0-2.3 2.3 0-2.6-4.5z" transform="rotate(240 16 16)"/></g></svg>`;

function parseHash(): { view: ViewId | null; material: string | null; state: string | null } {
  const h = location.hash.replace(/^#/, '');
  if (!h) return { view: null, material: null, state: null };
  if (h.startsWith('m=')) return { view: null, material: h.slice(2), state: null };
  if (h.startsWith('s=')) return { view: null, material: null, state: h.slice(2) };
  const view = VIEWS.find((v) => v.id === h)?.id ?? null;
  return { view, material: null, state: null };
}

function shell(app: HTMLElement, data: Dataset): void {
  const { meta } = data;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#overview">${LOGO}<span>Waste &amp; Recycling</span><span class="brand-sub">Australia · ${escapeHtml(fyShort(meta.dataFrom))}–${escapeHtml(fyShort(meta.dataThrough))}</span></a>
        <div class="search-wrap">
          <input class="search-input" type="search" placeholder="Search a material or state…" aria-label="Search materials and states" autocomplete="off" />
          <div class="search-results" role="listbox"></div>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" data-role="about" aria-label="About this site" title="About this site">?</button>
      </div>
    </header>
    <nav class="nav-tabs" aria-label="Views">
      <div class="nav-inner" role="tablist">
        ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
      </div>
    </nav>
    <main class="main-content" id="view-root"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>
          Data: <a href="${escapeHtml(meta.sources.dataset)}" target="_blank" rel="noopener">National Waste Report</a>
          (DCCEEW; CC&nbsp;BY&nbsp;4.0) · population &amp; boundaries ABS. ${escapeHtml(fyShort(meta.dataFrom))}–${escapeHtml(fyShort(meta.dataThrough))}.
        </span>
        <span>
          Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> ·
          <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        </span>
      </div>
    </footer>
  `;

  const viewRoot = app.querySelector('#view-root') as HTMLElement;
  const about = createAbout(meta);
  const drawers = createDrawers(data, (v) => show(v));
  let current: ViewId = 'overview';

  const ctx: ViewContext = {
    data,
    openMaterial: (k) => drawers.openMaterial(k),
    openState: (c) => drawers.openState(c),
    goTo: (v) => show(v),
  };

  function show(id: ViewId, pushHash = true): void {
    const view = VIEWS.find((v) => v.id === id) ?? VIEWS[0];
    drawers.close();
    document.querySelector('.hover-tip')?.classList.remove('visible');
    current = view.id;
    app.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('data-view') === current)));
    viewRoot.innerHTML = '';
    try {
      view.render(viewRoot, ctx);
    } catch {
      viewRoot.innerHTML = '<div class="error-box">Something went wrong rendering this view. Try another tab.</div>';
    }
    if (pushHash) history.replaceState(null, '', `#${current}`);
    window.scrollTo({ top: 0 });
  }

  app.querySelector('.nav-inner')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-view]');
    if (btn) show(btn.getAttribute('data-view') as ViewId);
  });
  app.querySelector('[data-role="about"]')?.addEventListener('click', () => about.open());
  app.querySelector('.brand')?.addEventListener('click', (e) => { e.preventDefault(); show('overview'); });

  // ── Header search ──
  const input = app.querySelector('.search-input') as HTMLInputElement;
  const results = app.querySelector('.search-results') as HTMLElement;
  let hits: SearchHit[] = [];
  let cursor = -1;
  const closeSearch = () => { results.innerHTML = ''; cursor = -1; };
  const pick = (h: SearchHit) => {
    if (h.kind === 'material') drawers.openMaterial(h.key);
    else drawers.openState(h.key);
    input.value = '';
    closeSearch();
  };
  const paint = () => {
    results.innerHTML = hits
      .map((h, i) => {
        const sw = h.kind === 'material' ? catInfo(h.key).colour : jurisColour(h.key);
        return `<button class="search-item" data-idx="${i}" role="option" aria-selected="${i === cursor}"><span class="si-sw" style="background:${sw}"></span><strong>${escapeHtml(h.label)}</strong><span>${escapeHtml(h.sub)}</span></button>`;
      })
      .join('');
  };
  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => { hits = search(data, input.value); cursor = -1; paint(); }, 300);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeSearch(); input.blur(); return; }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); cursor = (cursor + 1) % hits.length; paint(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); cursor = (cursor - 1 + hits.length) % hits.length; paint(); }
    else if (e.key === 'Enter') { e.preventDefault(); const h = hits[cursor >= 0 ? cursor : 0]; if (h) pick(h); }
  });
  results.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-idx]');
    if (!btn) return;
    const h = hits[Number(btn.getAttribute('data-idx'))];
    if (h) pick(h);
  });
  document.addEventListener('click', (e) => { if (!(e.target as Element).closest('.search-wrap')) closeSearch(); });

  // ── Routing ──
  const initial = parseHash();
  show(initial.view ?? 'overview', !(initial.material || initial.state));
  if (initial.material) drawers.openMaterial(initial.material);
  else if (initial.state) drawers.openState(initial.state);
  window.addEventListener('hashchange', () => {
    const { view, material, state } = parseHash();
    if (material) drawers.openMaterial(material);
    else if (state) drawers.openState(state);
    else if (view && view !== current) show(view, false);
  });
}

function renderError(app: HTMLElement, retry: () => void): void {
  app.innerHTML = `
    <main class="main-content">
      <div class="error-box">
        <strong>Could not load the data.</strong>
        <p style="margin-top:8px">Check your connection and try again.</p>
        <button type="button">Retry</button>
      </div>
    </main>`;
  app.querySelector('button')?.addEventListener('click', retry);
}

async function boot(): Promise<void> {
  const app = document.getElementById('app') as HTMLElement;
  app.innerHTML = `
    <main class="main-content">
      <div class="loading">Loading Australia's waste and recycling record…</div>
      <div class="skeleton"></div>
    </main>`;
  initTooltip();
  initGlossary();
  try {
    const data = await loadDataset();
    shell(app, data);
  } catch {
    renderError(app, () => { void boot(); });
  }
}

void boot();

export type { Dataset };
